import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useCallback, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { prepareWorkflowImport } from "../lib/prepareWorkflowImport";
import { navigate, routes } from "../lib/urls";
import { parseZip } from "../lib/zip";

export function useWorkflowZipImport(spaceId: string | null) {
  const z = useZero();
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const importWorkflowZip = useCallback(
    async (file: File) => {
      if (!spaceId || isImporting) return false;
      setImportError(null);
      setIsImporting(true);
      try {
        let raw: Record<string, string>;
        try {
          raw = await parseZip(file);
        } catch (err) {
          setImportError(
            err instanceof Error ? err.message : "Invalid zip file"
          );
          return false;
        }

        const prepared = prepareWorkflowImport(raw, file.name);
        if (!prepared.ok) {
          setImportError(prepared.error);
          return false;
        }

        const workflowId = nanoid(12);
        const branchId = nanoid(12);
        const commitId = nanoid(12);
        const chatId = nanoid(12);
        const systemEntryId = nanoid(12);

        const result = z.mutate(
          sharedMutators.importWorkflow({
            id: workflowId,
            spaceId,
            branchId,
            commitId,
            chatId,
            systemEntryId,
            name: prepared.derivedName,
            sourceFileName: file.name,
            files: prepared.files,
            apps: prepared.apps,
          })
        );
        const clientResult = (await result.client) as {
          type: string;
          error?: unknown;
        };
        if (clientResult.type === "error") {
          const e = clientResult.error;
          setImportError(
            e instanceof Error ? e.message : "Failed to import workflow"
          );
          return false;
        }
        navigate(routes.space.workflow, { spaceId, workflowId, branchId });
        return true;
      } finally {
        setIsImporting(false);
      }
    },
    [z, spaceId, isImporting]
  );

  return { importError, isImporting, importWorkflowZip };
}
